// React
import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';

// Amplify
import { Auth } from 'aws-amplify';

// Material UI
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import SaveIcon from '@mui/icons-material/Save';

// Services
import { contentfulService } from 'src/services/contentfulService';

// Data
// import timezones, { Timezone } from 'timezones.json';
import { timezonesEN, timezonesES } from 'src/data/timezones';

// Custom types
import LanguageOption from 'src/types/languageOption';
import UserSettingsFormProps from 'src/types/props/userSettingsFormProps';
import GatewayUser, { allAssignedStations } from 'src/types/gateway/gatewayUser';
import Timezone from 'src/types/timezone';

// Services
import { gatewayService } from 'src/services/gatewayService';

// Hooks and utils
import { useTranslation } from 'react-i18next';
import { appConfig } from 'src/config/config';
import { getLogMsg } from 'src/utils/loggingUtils';
import useMounted from 'src/hooks/useMounted';
import detectBrowserLanguage from 'detect-browser-language';
import logger from 'src/logging/logger';

const UserSettingsForm: FC<UserSettingsFormProps> = ({ isInitialSave, onSubmitChanges }: UserSettingsFormProps) => {
  // State variables
  const [gatewayUser, setSetGatewayUser] = useState<GatewayUser>();
  const [languages, setLanguages] = useState<LanguageOption[]>();
  const [timezones, setTimezones] = useState<Timezone[]>();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedTimezone, setSelectedTimezone] = useState<string>('');
  const [selectedPreferredStation, setSelectedPreferredStation] = useState<string>('');

  // Hooks and utils
  const { t: translate, i18n } = useTranslation();
  const mounted = useMounted();

  // Constants
  const { formFields } = appConfig.i18n;
  const { saveChanges } = appConfig.i18n.actions;
  const { contentfulEnglish, contentfulSpanish, i18nEnglish, i18nSpanish } = appConfig.i18n.languages;

  // Data
  const init = useCallback(async () => {
    try {
      const session = await Auth.currentSession();
      const { email } = session.getIdToken().payload;
      const locales = await contentfulService.getLocales();
      setLanguages(locales);
      if (isInitialSave) {
        gatewayService.getUser(email)
          .then(async (user) => {
            if (user) {
              setSetGatewayUser(user);
              const assignedStations = allAssignedStations(user);
              setSelectedPreferredStation(assignedStations.length > 0 ? assignedStations[0].site_id : '');
            }
          })
          .catch((err) => {
            logger.error(getLogMsg('init', err));
          });
        const browserLanguage = detectBrowserLanguage();
        // try to determine the user's language based on their browser settings
        if (browserLanguage.indexOf('es') > -1) {
          i18n.changeLanguage(i18nSpanish);
          setSelectedLanguage(contentfulSpanish);
          setTimezones(timezonesES);
        } else {
          i18n.changeLanguage(i18nEnglish);
          setSelectedLanguage(contentfulEnglish);
          setTimezones(timezonesEN);
        }
        // set timezone to central north amercia by default
        setSelectedTimezone('Pacific Daylight Time');
      } else {
        gatewayService.getUser(email)
          .then(async (user) => {
            setSetGatewayUser(user);
            setFirstName(user.first_name);
            setLastName(user.last_name);
            setCity(user.city);
            if (user.language === contentfulEnglish) {
              setTimezones(timezonesEN);
            } else {
              setTimezones(timezonesES);
            }
            setSelectedLanguage(user.language);
            setSelectedTimezone(user.timezone);
            setSelectedPreferredStation(user.default_station);
          })
          .catch((err) => {
            logger.error(getLogMsg('init', err));
          });
      }
    } catch (err) {
      gatewayService.logErrorActivity(err);
      logger.error(getLogMsg('User Settings - init', err));
    }
  }, [mounted]);

  const siteLanguageChange = useCallback(async () => {
    try {
      if (i18n.language === contentfulEnglish) {
        setTimezones(timezonesEN);
      } else {
        setTimezones(timezonesES);
      }
    } catch (err) {
      logger.error(getLogMsg('User Settings - siteLanguageChange', err));
    }
  }, [mounted]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    siteLanguageChange();
  }, [i18n.language]);

  // Event Handlers
  const handleLanguageChange = (event) => {
    setSelectedLanguage(event.target.value);
  };

  const handlePrefferredStationChange = (event) => {
    setSelectedPreferredStation(event.target.value);
  };

  const handleTimezoneChange = (event) => {
    setSelectedTimezone(event.target.value);
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handleCityChange = (event) => {
    setCity(event.target.value);
  };

  const handleSubmit = (event) => {
    onSubmitChanges(firstName,
      lastName,
      city,
      selectedLanguage,
      selectedTimezone,
      selectedPreferredStation,
      event);
  };

  return (
    <Box sx={{ mx: 2 }}>
      <Grid container>
        {isInitialSave
        && (
          <Grid
            item
            xs={12}
            sx={{
              pb: 2
            }}
          >
            <Typography variant="h5">
              {translate(formFields.tellUsMore)}
            </Typography>
          </Grid>
        )}
        <Grid
          item
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{
            p: 1
          }}
        >
          <TextField
            fullWidth
            value={firstName}
            label={translate(formFields.firstName)}
            onChange={handleFirstNameChange}
            size="small"
          />
        </Grid>
        <Grid
          item
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{
            p: 1
          }}
        >
          <TextField
            fullWidth
            value={lastName}
            label={translate(formFields.lastName)}
            onChange={handleLastNameChange}
            size="small"
          />
        </Grid>
        <Grid
          item
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{
            p: 1
          }}
        >
          <TextField
            fullWidth
            value={city}
            label={translate(formFields.city)}
            onChange={handleCityChange}
            size="small"
          />
        </Grid>
        {gatewayUser
        && !gatewayUser.is_admin
        && !gatewayUser.is_super
        && !gatewayUser.is_tm
        && (gatewayUser.mdm_stations || gatewayUser.stations)
        && (gatewayUser.mdm_stations.length > 0 || gatewayUser.stations.length > 0)
        && (
          <Grid
            item
            lg={8}
            md={8}
            sm={12}
            xs={12}
            sx={{
              p: 1
            }}
          >
            <Box>
              <FormControl fullWidth>
                <InputLabel id="select-language-label">{translate(formFields.preferredStation)}</InputLabel>
                <Select
                  multiple={false}
                  value={selectedPreferredStation}
                  label={translate(formFields.preferredStation)}
                  fullWidth
                  onChange={handlePrefferredStationChange}
                  size="small"
                >
                  {allAssignedStations(gatewayUser).map((station) => (
                    <MenuItem
                      key={station.site_id}
                      value={station.site_id}
                    >
                      <ListItemText primary={station.site_description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        )}
        <Grid
          item
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{
            p: 1
          }}
        >
          <Box>
            <FormControl fullWidth>
              <InputLabel id="select-language-label">{translate(formFields.language)}</InputLabel>
              <Select
                multiple={false}
                value={selectedLanguage}
                label={translate(formFields.language)}
                fullWidth
                onChange={handleLanguageChange}
                size="small"
              >
                {languages && languages.map((language: LanguageOption) => (
                  <MenuItem
                    disableGutters
                    key={language.code}
                    value={language.code}
                  >
                    <ListItemText primary={language.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid
          item
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{
            p: 1
          }}
        >
          <Box>
            <FormControl fullWidth>
              <InputLabel id="select-timezone-label">{translate(formFields.timezone)}</InputLabel>
              <Select
                multiple={false}
                value={selectedTimezone}
                label={translate(formFields.timezone)}
                fullWidth
                onChange={handleTimezoneChange}
                size="small"
              >
                {timezones && timezones.map((timezone: Timezone) => (
                  <MenuItem
                    key={timezone.text}
                    value={timezone.value}
                  >
                    <ListItemText primary={timezone.text} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid
          item
          textAlign="right"
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{
            p: 1
          }}
        >
          <Button
            onClick={handleSubmit}
            variant="outlined"
            endIcon={<SaveIcon />}
            color="primary"
          >
            {translate(saveChanges)}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserSettingsForm;
